import {
  ReactElement,
  useMemo,
  useState,
  ChangeEvent,
  FocusEvent,
  forwardRef,
  Ref,
  RefAttributes,
  type JSX,
  useImperativeHandle,
  useCallback,
  useRef,
  useEffect,
} from "react";

import { InputBaseComponentProps } from "@mui/material/InputBase";
import {
  getZeroAmountPlaceholder,
  formatCurrency,
  getDollarValue,
  isCurrencySymbolAfterAmount,
  AvailablePaymentCurrency,
  formatDisplayValue,
  parseDefaultDisplayValue,
  parseInputValue,
  sanitizeAmountInput,
  stripGroupSeparators,
  unformatDisplayValue,
} from "@simplyk/common";
import { FieldValues, useController, useWatch } from "react-hook-form";

import { getLabel, isInputRequired } from "../../../common/helpers/validators";
import { DashboardTextField } from "../TextField/DashboardTextField";

import { AmountInputHandle, AmountInputProps, FormAmountInputProps, BaseAmountInputProps } from "./types";

import { useFormInputRefs } from "@/common/hooks/useFormInputRefs";
import { useValidators } from "@/common/hooks/useValidators";

function AmountInputInner<T extends FieldValues>(
  { control, name, rules, shouldUnregister, ...rest }: AmountInputProps<T>,
  ref: Ref<AmountInputHandle>
) {
  return control && name ? (
    <FormAmountInput
      {...rest}
      ref={ref}
      control={control}
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  ) : (
    <BaseAmountInput {...rest} name={name} ref={ref} />
  );
}

export const DashboardAmountInput = forwardRef(AmountInputInner) as unknown as <T extends FieldValues>(
  props: AmountInputProps<T> & RefAttributes<AmountInputHandle>
) => JSX.Element;

function InternalFormAmountInput<T extends FieldValues>(
  {
    control,
    defaultValue,
    inputRef,
    name,
    onBlur,
    onChange,
    rules,
    shouldUnregister,
    noAsterisk,
    ...rest
  }: FormAmountInputProps<T>,
  ref: Ref<AmountInputHandle>
) {
  const watchedValue = useWatch({ control, name });
  const watchedDefaultValue = watchedValue !== undefined ? watchedValue : defaultValue;
  const baseAmountRef = useRef<AmountInputHandle>(null);

  const validators = useValidators(rules);
  const isRequired = isInputRequired(validators);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: watchedDefaultValue as never,
    rules: validators,
    shouldUnregister,
  });

  const handleChange = useCallback(
    (centsValue: number | null) => {
      field.onChange(centsValue);
      onChange?.(centsValue);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    onBlur?.();
  }, [field, onBlur]);

  useImperativeHandle(
    ref,
    () => ({
      editValue: (value: number) => {
        baseAmountRef.current?.editValue(value);
        const parsedValue = value || 0;
        if (parsedValue && parsedValue !== watchedValue) {
          field.onChange(parsedValue);
        }
      },
    }),
    [field, watchedValue]
  );

  const refCallBack = useFormInputRefs({ inputRefCallBack: field.ref, inputRef });

  return (
    <BaseAmountInput
      {...rest}
      defaultValue={watchedDefaultValue}
      inputRef={refCallBack}
      onChange={handleChange}
      ref={baseAmountRef}
      onBlur={handleBlur}
      label={getLabel(isRequired, rest.label, noAsterisk)}
      name={name}
    />
  );
}

InternalFormAmountInput.displayName = "FormAmountInput";

const FormAmountInput = forwardRef(InternalFormAmountInput) as unknown as <T extends FieldValues>(
  props: FormAmountInputProps<T> & RefAttributes<AmountInputHandle>
) => JSX.Element;

const InternalBaseAmountInput = (
  {
    currency = AvailablePaymentCurrency.Usd,
    debounced,
    defaultValue,
    inputProps,
    locale,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    name,
    endAdornment,
    ...rest
  }: BaseAmountInputProps,
  ref: Ref<AmountInputHandle>
): ReactElement<unknown> => {
  const currentPlaceholder = placeholder || getZeroAmountPlaceholder(locale);

  const displayedDefaultValue = parseDefaultDisplayValue(defaultValue, locale);
  const [displayedValue, setDisplayedValue] = useState<string>(displayedDefaultValue);
  const isFocusedRef = useRef(false);
  const lastEmittedValueRef = useRef<number | null | undefined>(undefined);
  const hasSkippedExternalSyncRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayedValue(parseDefaultDisplayValue(defaultValue, locale));
      hasSkippedExternalSyncRef.current = false;
    } else {
      hasSkippedExternalSyncRef.current = (defaultValue ?? null) !== (lastEmittedValueRef.current ?? null);
    }
  }, [defaultValue, locale]);

  const setValue = useCallback(
    (centsValue: number | null) => {
      lastEmittedValueRef.current = centsValue;
      onChange?.(centsValue);
    },
    [onChange]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const sanitizedValue = sanitizeAmountInput(event.target.value);
      setValue(parseInputValue(sanitizedValue, locale));
      if (!debounced) {
        setDisplayedValue(sanitizedValue);
      }
    },
    [debounced, locale, setValue]
  );

  const handleChangeNotDebounced = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDisplayedValue(sanitizeAmountInput(event.target.value));
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (hasSkippedExternalSyncRef.current) {
      hasSkippedExternalSyncRef.current = false;
      setDisplayedValue(parseDefaultDisplayValue(defaultValue, locale));
    } else {
      setDisplayedValue((value) => formatDisplayValue(value, locale));
    }
    onBlur?.();
  }, [defaultValue, locale, onBlur]);

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      isFocusedRef.current = true;
      setDisplayedValue((value) => stripGroupSeparators(value, locale));
      onFocus?.(event);
    },
    [locale, onFocus]
  );

  const currentInputProps: InputBaseComponentProps = useMemo(
    () => ({
      ...inputProps,
      min: 0,
      inputMode: "decimal",
      step: "any",
      onWheel: (event) => {
        event.currentTarget.blur();
      },
      onKeyPress: (event) => {
        if (event.key.length === 1 && !/[\d.,\s]/.test(event.key)) {
          event.preventDefault();
        }
      },
    }),
    [inputProps]
  );

  useImperativeHandle(
    ref,
    () => ({
      editValue: (value: number) => {
        const currentInputValue = getDollarValue(parseFloat(unformatDisplayValue(displayedValue, locale)) || 0);
        if (value && value !== currentInputValue) {
          setDisplayedValue(parseDefaultDisplayValue(value, locale));
        }
      },
    }),
    [displayedValue, locale]
  );

  const displayedCurrency = <span>{formatCurrency(locale, currency)}</span>;
  const isSymbolAfterAmount = isCurrencySymbolAfterAmount(locale);

  return (
    <DashboardTextField
      {...rest}
      debounced={debounced}
      startAdornment={isSymbolAfterAmount ? undefined : displayedCurrency}
      endAdornment={endAdornment ? endAdornment : isSymbolAfterAmount ? displayedCurrency : undefined}
      inputProps={currentInputProps}
      onChange={handleChange}
      onChangeNotDebounced={handleChangeNotDebounced}
      placeholder={currentPlaceholder}
      ref={ref as Ref<HTMLInputElement>}
      value={displayedValue}
      onBlur={handleBlur}
      onFocus={handleFocus}
      type="text"
      name={name}
    />
  );
};

const BaseAmountInput = forwardRef(InternalBaseAmountInput);
